import * as React from "react";
import {
  Box,
  IconButton,
  Grid,
  Button as MUIButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Typography,
} from "@mui/material";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MainInput, main_button } from "../app/components/Styles";
import { Pagination } from "antd";
import { Close, Search } from "@mui/icons-material";
import moment from "moment";
import { getCashCycles, getNextPayout, getTransactions, exportTransactions } from "../app/store/actions/dataActions";
import { useEffect } from "react";
import DateRangePicker from "../app/components/DateRangePicker";
import TransactionDetailsModal from "../app/components/TransactionDetailsModal";
import CreateTicketModal from "../app/components/CreateTicketModal";
import { useTranslation } from "react-i18next";
import Hoverable from "../app/glass/Hoverable";
import { formatEgp, fieldLabel } from "../app/glass/styles";
import SearchFilterBar, { exportIcon } from "../app/glass/SearchFilterBar";
import { useIsMobile } from "../app/hooks/useMediaQuery";
import { getOrderTrackingId } from "../app/utils/shippingDisplay";

const CHIP_KEYS = ['all', 'cashcollection', 'cashouts', 'recharge', 'compensations', 'deductions', 'others'];
const LEGACY_TYPE = {
  orders: 'cashcollection',
  'fees-compensations': 'deductions',
  payouts: 'cashouts',
};

const parseChip = (raw) => {
  if (!raw || raw === 'undefined') return 'all';
  if (LEGACY_TYPE[raw]) return LEGACY_TYPE[raw];
  return CHIP_KEYS.includes(raw) ? raw : 'all';
};

const parseDateFromUrl = (dateString) => {
  if (!dateString || dateString === 'Invalid date' || dateString === 'undefined') return undefined;
  const parsed = moment(dateString, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed : undefined;
};

const cleanSearchParams = (params) => {
  const cleaned = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '' && value !== 'undefined' && value !== 'Invalid date') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

const orderNet = (record) => (
  record.transactions?.reduce((sum, tx) => {
    const value = parseFloat(tx.value || 0);
    if (tx.type === 'COD') return sum + value;
    if (['Fee', 'ShippingFee'].includes(tx.type)) return sum - value;
    if (tx.type === 'Compensation') return sum + value;
    return sum + value;
  }, 0) || 0
);

const rowAmount = (record) => {
  if (record.dataType === 'payout') return -parseFloat(record.value || 0);
  if (record.dataType === 'fee') {
    const value = parseFloat(record.value || 0);
    return (record.type === 'Compensation' || record.type === 'Recharge') ? value : -Math.abs(value);
  }
  return orderNet(record);
};

const rowDate = (record) => {
  if (record.dataType === 'payout' || record.dataType === 'fee') return record.date;
  return record.transactions?.[0]?.createdAt || record.order?.createdAt;
};

export const Transactions = ({
  getTransactions,
  exportTransactions,
  user,
  transactions,
  transactionsPagination,
  getNextPayout,
  payoutDue,
  expectedIncome,
  totalIncome,
  totalFees,
  totalHold,
  totalPayout,
  payouts,
  fees,
  getCashCycles,
  cashCycles,
  isLoading,
  isWaiting,
  token
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const urlStartDate = parseDateFromUrl(searchParams.get('startDate'));
  const urlEndDate = parseDateFromUrl(searchParams.get('endDate'));

  const [filters, setFilters] = React.useState({
    startDate: urlStartDate || moment().startOf('month'),
    endDate: urlEndDate || moment().endOf('month'),
    cashoutId: searchParams.get('cashoutId') && searchParams.get('cashoutId') !== 'undefined' ? searchParams.get('cashoutId') : undefined,
  });
  const [search, setSearch] = React.useState(searchParams.get('search') || '');
  const [page, setPage] = React.useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = React.useState(parseInt(searchParams.get('pageSize')) || 10);
  const [activeTab, setActiveTab] = React.useState(parseChip(searchParams.get('type')));
  const [selectedTransaction, setSelectedTransaction] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [breakdownOpen, setBreakdownOpen] = React.useState(false);
  const [showFilters, setShowFilters] = React.useState(false);
  const [exportModalOpen, setExportModalOpen] = React.useState(false);
  const [exportParams, setExportParams] = React.useState({
    startDate: null,
    endDate: null,
    cashoutId: undefined,
  });
  const [helpTicketOpen, setHelpTicketOpen] = React.useState(false);
  const [helpTicketContext, setHelpTicketContext] = React.useState(null);

  const cycleOptions = Array.isArray(cashCycles) ? cashCycles : cashCycles?.docs || cashCycles?.cashCycles || [];

  const fetchTransactions = (overrides = {}) => {
    const pageToUse = overrides.page !== undefined ? overrides.page : page;
    const pageSizeToUse = overrides.pageSize !== undefined ? overrides.pageSize : pageSize;
    const params = cleanSearchParams({
      startDate: filters.startDate && filters.startDate.isValid() ? filters.startDate.format('YYYY-MM-DD') : undefined,
      endDate: filters.endDate && filters.endDate.isValid() ? filters.endDate.format('YYYY-MM-DD') : undefined,
      cashoutId: filters.cashoutId,
      page: pageToUse,
      limit: pageSizeToUse,
      search: search || undefined,
    });
    getTransactions(params);
  };

  useEffect(() => {
    getNextPayout({});
    getCashCycles({});
    if ((filters.startDate && filters.endDate) || (!filters.startDate && !filters.endDate)) {
      fetchTransactions();
    }
    // eslint-disable-next-line
  }, []);

  const pushParams = (extra = {}) => {
    const newParams = cleanSearchParams({
      page: String(extra.page ?? page),
      pageSize: String(extra.pageSize ?? pageSize),
      type: extra.type ?? activeTab,
      startDate: filters.startDate && filters.startDate.isValid() ? filters.startDate.format('YYYY-MM-DD') : undefined,
      endDate: filters.endDate && filters.endDate.isValid() ? filters.endDate.format('YYYY-MM-DD') : undefined,
      cashoutId: filters.cashoutId,
      search: search || undefined,
      ...extra.url,
    });
    setSearchParams(newParams);
  };

  const handleChipChange = (key) => {
    setActiveTab(key);
    setPage(1);
    pushParams({ page: 1, type: key });
    if (key === 'cashcollection' || key === 'all') {
      fetchTransactions({ page: 1 });
    }
  };

  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setFilters((prev) => ({
      startDate: newStartDate && newStartDate.isValid() ? newStartDate : undefined,
      endDate: newEndDate && newEndDate.isValid() ? newEndDate : undefined,
      cashoutId: prev.cashoutId,
    }));
  };
  const handleCashoutIdChange = (value) => {
    setFilters((prev) => ({ ...prev, cashoutId: value?.title || undefined }));
  };

  const handleFilter = () => {
    setPage(1);
    pushParams({ page: 1 });
    if ((filters.startDate && filters.endDate) || (!filters.startDate && !filters.endDate)) {
      fetchTransactions({ page: 1 });
    }
  };

  const handleTableChange = (nextPage, nextSize) => {
    const size = nextSize || pageSize;
    setPage(nextPage);
    setPageSize(size);
    pushParams({ page: nextPage, pageSize: size });
    if (activeTab === 'cashcollection' || activeTab === 'all') {
      if ((filters.startDate && filters.endDate) || (!filters.startDate && !filters.endDate)) {
        fetchTransactions({ page: nextPage, pageSize: size });
      }
    }
  };

  const clearAllFilters = () => {
    setFilters({ startDate: undefined, endDate: undefined, cashoutId: undefined });
    setSearch('');
    setPage(1);
    setSearchParams({
      page: '1',
      pageSize: pageSize.toString(),
      type: activeTab,
    });
    getTransactions(cleanSearchParams({ page: 1, limit: pageSize }));
  };

  function getNextCashOutDate() {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const cashOutDays = [0, 2, 4];
    let daysToAdd = 0;
    for (let i = 0; i < cashOutDays.length; i++) {
      if (cashOutDays[i] > dayOfWeek) {
        daysToAdd = cashOutDays[i] - dayOfWeek;
        break;
      }
    }
    if (daysToAdd === 0) {
      daysToAdd = cashOutDays[0] - dayOfWeek + 7;
    }
    const nextDate = new Date();
    nextDate.setDate(today.getDate() + daysToAdd);
    nextDate.setHours(0, 0, 0, 0);
    return nextDate;
  }

  const handleShowDetails = (record) => {
    if (record.dataType === 'payout' || record.dataType === 'fee') {
      setSelectedTransaction({
        transaction: { ...record, dataType: record.dataType },
        order: null,
        transactions: null
      });
    } else {
      setSelectedTransaction({
        transaction: null,
        order: record.order,
        transactions: record.transactions
      });
    }
    setModalOpen(true);
  };

  const mapOrders = () => (
    transactions?.map((row) => ({
      _id: row.order._id,
      order: row.order,
      transactions: row.transactions,
      cashoutId: row.transactions[0]?.cashoutId,
      shopifyOrderNumber: row.order.shopifyOrderNumber,
      chainzFinanceStatus: row.order.chainzFinanceStatus,
      trackingId: getOrderTrackingId(row.order),
      dataType: 'order',
    })) || []
  );

  const mapFees = () => (
    fees?.map((row) => ({
      _id: row._id,
      date: row.createdAt,
      value: row.value,
      type: row.type,
      service: row.service,
      description: row.service,
      proof: row.proof,
      feeType: row.feeType,
      dataType: 'fee',
    })) || []
  );

  const mapPayouts = () => (
    payouts?.map((row) => ({
      _id: row._id,
      date: row.createdAt,
      value: row.value,
      type: row.type,
      service: row.service,
      cashoutId: row.cashoutId || '',
      proof: row.proof,
      dataType: 'payout',
    })) || []
  );

  const getFilteredData = () => {
    let orderRows = mapOrders();
    if (filters.cashoutId) {
      orderRows = orderRows.filter((row) => row.cashoutId === filters.cashoutId);
    }
    const feeRows = mapFees();
    const payoutRows = mapPayouts();

    switch (activeTab) {
      case 'cashcollection':
        return orderRows;
      case 'cashouts':
        return payoutRows;
      case 'recharge':
        return feeRows.filter((row) => row.type === 'Recharge');
      case 'compensations':
        return feeRows.filter((row) => row.type === 'Compensation');
      case 'deductions':
        return feeRows.filter((row) => row.type === 'Fee');
      case 'others':
        return feeRows.filter((row) => !['Recharge', 'Compensation', 'Fee'].includes(row.type));
      case 'all':
      default:
        return [...orderRows, ...feeRows, ...payoutRows];
    }
  };

  const handleExportToExcel = () => {
    setExportParams({
      startDate: filters.startDate || null,
      endDate: filters.endDate || null,
      cashoutId: filters.cashoutId || undefined,
    });
    setExportModalOpen(true);
  };

  const handleExportConfirm = () => {
    const params = cleanSearchParams({
      startDate: exportParams.startDate && exportParams.startDate.isValid() ? exportParams.startDate.format('YYYY-MM-DD') : undefined,
      endDate: exportParams.endDate && exportParams.endDate.isValid() ? exportParams.endDate.format('YYYY-MM-DD') : undefined,
      cashoutId: exportParams.cashoutId,
      search: search || undefined,
    });
    exportTransactions(params);
    setExportModalOpen(false);
  };

  const handleExportDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setExportParams({
      ...exportParams,
      startDate: newStartDate || null,
      endDate: newEndDate || null
    });
  };

  const nextPayoutDate = getNextCashOutDate();
  const allFilteredData = getFilteredData();
  const clientPaged = activeTab !== 'cashcollection' && activeTab !== 'all';
  const visibleRows = clientPaged
    ? allFilteredData.slice((page - 1) * pageSize, page * pageSize)
    : allFilteredData;
  const pageTotal = activeTab === 'cashcollection'
    ? (transactionsPagination?.totalDocs || allFilteredData.length)
    : allFilteredData.length;

  const creditTotal = Number(totalIncome) || 0;
  const debitTotal = (Number(totalFees) || 0) + (Number(totalHold) || 0) + (Number(totalPayout) || 0);

  const chips = [
    ['all', t('transactions.chips.all')],
    ['cashcollection', t('transactions.chips.cashCollection')],
    ['cashouts', t('transactions.chips.cashouts')],
    ['recharge', t('transactions.chips.recharge')],
    ['compensations', t('transactions.chips.compensations')],
    ['deductions', t('transactions.chips.deductions')],
    ['others', t('transactions.chips.others')],
  ];

  const rowMeta = (record) => {
    const amount = rowAmount(record);
    const positive = amount >= 0;
    const when = rowDate(record);
    let id = record._id?.slice?.(-8) || t('common.na');
    let category = t('transactions.category.order');
    let detail = '';
    if (record.dataType === 'payout') {
      id = record.cashoutId || id;
      category = t('transactions.category.cashOut');
      detail = t('transactions.category.bankTransfer');
    } else if (record.dataType === 'fee') {
      category = record.type || t('transactions.category.fee');
      detail = record.description || '';
    } else {
      id = record.order?.shopifyOrderNumber || id;
      category = record.order?.financialStatus || t('transactions.category.order');
      detail = record.cashoutId || record.order?.status || '';
    }
    return {
      amount,
      positive,
      id,
      category,
      detail,
      date: when ? moment(when).format('dddd, D MMM') : t('common.na'),
      time: when ? moment(when).format('h:mm A') : '',
      action: record.dataType === 'order' ? t('transactions.viewBreakdown') : t('transactions.showDetails'),
    };
  };

  const lineRow = (label, value, { top, indent } = {}) => (
    <div
      key={label}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        padding: '9px 4px',
        fontSize: 13,
        borderBottom: '1px solid var(--s05)',
        color: top ? 'var(--tx)' : 'var(--mut2)',
        fontWeight: top ? 600 : 400,
        paddingInlineStart: indent ? 16 : 0,
      }}
    >
      {label}
      <span style={{ marginInlineStart: 'auto', fontWeight: 600 }}>{formatEgp(value)} EGP</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        className="cz-g1"
        style={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 1fr 1fr',
          gap: 20,
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            borderRadius: 26,
            padding: '26px 30px',
            background: 'var(--card-solid)',
            border: '1px solid var(--s12)',
            boxShadow: 'inset 0 1px 0 var(--s18), 0 22px 50px var(--sh45)',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ fontSize: 12, color: 'var(--red-tx3)', marginBottom: 8 }}>{t('transactions.totalBalance')}</div>
          <div style={{ fontSize: isMobile ? 28 : 40, fontWeight: 600 }}>
            {isLoading ? '—' : `${formatEgp(payoutDue)} EGP`}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
            <button
              type="button"
              className={`cz-chip ${breakdownOpen ? 'on' : ''}`}
              onClick={() => setBreakdownOpen((v) => !v)}
            >
              {t('transactions.viewBreakdown')}
            </button>
          </div>
        </div>

        <div
          style={{
            borderRadius: 24,
            padding: '22px 24px',
            background: 'var(--card-solid)',
            border: '1px solid var(--s10)',
            boxShadow: 'inset 0 1px 0 var(--s10), 0 18px 44px var(--sh40)',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{t('finance.cashOutFrequency')}</div>
            <div
              style={{
                marginInlineStart: 'auto',
                fontSize: 10,
                padding: '3px 10px',
                borderRadius: 999,
                background: 'rgba(125,1,12,0.45)',
                border: '1px solid rgba(190,50,62,0.45)',
                color: 'var(--red-tx2)',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {t('finance.scheduleSunTueThu')}
            </div>
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--mut2)' }}>
            {t('finance.cashOutFrequencyHint')}
          </div>
          <Hoverable
            base={{ marginTop: 14, fontSize: 12.5, color: 'var(--red-tx3)', cursor: 'pointer' }}
            hover={{ color: 'var(--txs)' }}
            onClick={() => navigate('/settings?tab=pricing')}
          >
            {t('finance.changePlan')}
          </Hoverable>
        </div>

        <div
          style={{
            borderRadius: 24,
            padding: '22px 24px',
            background: 'var(--card-solid)',
            border: '1px solid var(--s10)',
            boxShadow: 'inset 0 1px 0 var(--s10), 0 18px 44px var(--sh40)',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>{t('transactions.nextCashOutDate')}</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--mut2)' }}>
            {t('transactions.nextCashOutHint')}
          </div>
          <div
            style={{
              marginTop: 14,
              display: 'inline-flex',
              padding: '8px 16px',
              borderRadius: 999,
              background: 'var(--s05)',
              border: '1px solid var(--s12)',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {moment(nextPayoutDate).format('dddd, D MMM')}
          </div>
          {expectedIncome != null && (
            <div style={{ marginTop: 14, fontSize: 12, color: 'var(--mut)' }}>
              {t('transactions.expectedIncome')}
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--tx)', marginTop: 4 }}>
                {formatEgp(expectedIncome)} EGP
              </div>
            </div>
          )}
        </div>
      </div>

      {breakdownOpen && (
        <div className="cz-g1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div
            style={{
              borderRadius: 24,
              padding: '24px 26px',
              background: 'var(--card-solid)',
              border: '1px solid var(--s10)',
              boxShadow: 'inset 0 1px 0 var(--s10), 0 18px 44px var(--sh40)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 9,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'rgba(172,187,198,0.14)',
                  border: '1px solid rgba(172,187,198,0.30)',
                  color: '#ACBBC6',
                  fontWeight: 700,
                }}
              >
                +
              </div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                {t('modals.credit')} · <span style={{ color: '#ACBBC6' }}>{formatEgp(creditTotal)} EGP</span>
              </div>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--mut)', marginBottom: 16 }}>{t('modals.creditDescription')}</div>
            {lineRow(t('modals.totalIncome'), creditTotal, { top: true })}
          </div>
          <div
            style={{
              borderRadius: 24,
              padding: '24px 26px',
              background: 'var(--card-solid)',
              border: '1px solid var(--s10)',
              boxShadow: 'inset 0 1px 0 var(--s10), 0 18px 44px var(--sh40)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 9,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'rgba(125,1,12,0.45)',
                  border: '1px solid rgba(190,50,62,0.45)',
                  color: 'var(--red-tx2)',
                  fontWeight: 700,
                }}
              >
                −
              </div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                {t('modals.debit')} · <span style={{ color: '#e2707c' }}>{formatEgp(debitTotal)} EGP</span>
              </div>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--mut)', marginBottom: 16 }}>{t('modals.debitDescription')}</div>
            {lineRow(t('modals.fees'), totalFees, { top: true })}
            {lineRow(t('modals.hold'), totalHold, { indent: true })}
            {lineRow(t('modals.payouts'), totalPayout, { top: true })}
          </div>
        </div>
      )}

      <div className="cz-wrap" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {chips.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`cz-chip ${activeTab === key ? 'on' : ''}`}
            onClick={() => handleChipChange(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <SearchFilterBar
        placeholder={t('common.search')}
        value={search}
        onChange={setSearch}
        onSearch={handleFilter}
        isMobile={isMobile}
        filterOpen={showFilters}
        onToggleFilter={() => setShowFilters((v) => !v)}
        filterLabel={t('common.filter')}
        actions={(
          <button
            type="button"
            className="cz-chip"
            onClick={handleExportToExcel}
            disabled={isWaiting || !allFilteredData.length}
            title={t('transactions.exportTransactions')}
          >
            {t('common.export')}
            {exportIcon}
          </button>
        )}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={fieldLabel}>{t('dateRange.selectDateRange')}</div>
            <DateRangePicker
              startDate={filters.startDate}
              endDate={filters.endDate}
              onChange={handleDateRangeChange}
              fullWidth
              margin="normal"
            />
          </div>
          {(activeTab === 'cashcollection' || activeTab === 'all') && (
            <div>
              <div style={fieldLabel}>{t('transactions.cashoutId')}</div>
              <Autocomplete
                options={cycleOptions}
                fullWidth
                onChange={(_, value) => handleCashoutIdChange(value)}
                getOptionLabel={(cashCycle) => cashCycle.title || ''}
                renderInput={(params) => <MainInput {...params} margin="normal" fullWidth />}
                value={cycleOptions.find((c) => c.title === filters.cashoutId) || null}
              />
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="cz-chip on" onClick={handleFilter} disabled={isWaiting}>
              {t('common.applyFilters')}
            </button>
            <button type="button" className="cz-chip" onClick={clearAllFilters}>
              {t('common.clearAll')}
            </button>
          </div>
        </div>
      </SearchFilterBar>

      <div className="cz-scroll-x">
        <div style={{ minWidth: 820 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '48px 150px 170px 1.4fr 150px 130px',
              gap: 12,
              fontSize: 11,
              color: 'var(--mut)',
              padding: '0 22px',
            }}
          >
            <div />
            <div>{t('transactions.table.transactionId')}</div>
            <div>{t('common.date')}</div>
            <div>{t('common.category')}</div>
            <div>{t('common.amount')}</div>
            <div />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
            {isLoading && <div style={{ color: 'var(--mut)', padding: '20px 22px' }}>{t('common.loading')}</div>}
            {!isLoading && visibleRows.length === 0 && (
              <div
                style={{
                  padding: '44px 20px',
                  textAlign: 'center',
                  color: 'var(--mut)',
                  fontSize: 13.5,
                  borderRadius: 18,
                  border: '1px dashed var(--s12)',
                }}
              >
                {t('transactions.noInCategory')}
              </div>
            )}
            {!isLoading && visibleRows.map((record) => {
              const meta = rowMeta(record);
              const sign = meta.positive ? '+' : '−';
              return (
                <Hoverable
                  key={record._id}
                  base={{
                    display: 'grid',
                    gridTemplateColumns: '48px 150px 170px 1.4fr 150px 130px',
                    gap: 12,
                    alignItems: 'center',
                    padding: '13px 22px',
                    borderRadius: 18,
                    border: '1px solid transparent',
                    cursor: 'pointer',
                  }}
                  hover={{
                    background: 'linear-gradient(120deg, var(--s05), var(--s015))',
                    border: '1px solid var(--s08)',
                  }}
                  onClick={() => handleShowDetails(record)}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 9,
                      display: 'grid',
                      placeItems: 'center',
                      background: meta.positive ? 'rgba(172,187,198,0.12)' : 'rgba(125,1,12,0.40)',
                      border: `1px solid ${meta.positive ? 'rgba(172,187,198,0.30)' : 'rgba(190,50,62,0.45)'}`,
                      color: meta.positive ? '#ACBBC6' : 'var(--red-tx2)',
                      fontWeight: 700,
                      fontSize: 14,
                    }}
                  >
                    {sign}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{meta.id}</div>
                  <div>
                    <div style={{ fontSize: 13 }}>{meta.date}</div>
                    <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 2 }}>{meta.time}</div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>{meta.category}</div>
                    {meta.detail ? (
                      <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {meta.detail}
                      </div>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: meta.positive ? '#ACBBC6' : '#e2707c' }}>
                    {sign}{formatEgp(Math.abs(meta.amount))} EGP
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }} onClick={(e) => e.stopPropagation()}>
                    <Hoverable
                      as="button"
                      type="button"
                      base={{
                        padding: '7px 14px',
                        borderRadius: 999,
                        fontSize: 11.5,
                        color: 'var(--tx3)',
                        background: 'linear-gradient(160deg, var(--s10), var(--s03))',
                        border: '1px solid var(--s12)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                      hover={{ background: 'var(--s12)' }}
                      onClick={() => handleShowDetails(record)}
                    >
                      {meta.action}
                    </Hoverable>
                    <Hoverable
                      as="button"
                      type="button"
                      base={{
                        padding: '7px 10px',
                        borderRadius: 999,
                        fontSize: 11.5,
                        color: 'var(--tx3)',
                        background: 'linear-gradient(160deg, var(--s10), var(--s03))',
                        border: '1px solid var(--s12)',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                      hover={{ background: 'var(--s12)' }}
                      onClick={() => {
                        if (record.order) {
                          setHelpTicketContext({ ticketType: 'Order', relevantOrder: record.order._id });
                        } else {
                          setHelpTicketContext({ ticketType: 'Finance', relevantTransaction: record._id });
                        }
                        setHelpTicketOpen(true);
                      }}
                    >
                      {t('helpDesk.getHelp')}
                    </Hoverable>
                  </div>
                </Hoverable>
              );
            })}
          </div>
        </div>
      </div>

      {pageTotal > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={pageTotal}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            showTotal={(total) => `${t('table.total')} ${total} ${t('table.items')}`}
            onChange={handleTableChange}
            onShowSizeChange={handleTableChange}
          />
        </div>
      )}

      <TransactionDetailsModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedTransaction(null);
        }}
        transaction={selectedTransaction?.transaction}
        order={selectedTransaction?.order}
        vendor={user?.vendor}
        transactions={selectedTransaction?.transactions}
        showProof={selectedTransaction?.transaction?.dataType !== 'order'}
      />

      <Dialog
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'var(--card-solid)',
            color: 'var(--tx)',
            borderRadius: '24px',
            border: '1px solid var(--s10)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 17, fontWeight: 600 }}>{t('transactions.exportTransactions')}</span>
            <MUIButton onClick={() => setExportModalOpen(false)} sx={{ minWidth: 'auto', p: 0.5, color: 'var(--tx)' }}>
              <Close />
            </MUIButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: 'var(--mut)', mb: 0.5 }}>
                  {t('dateRange.selectDateRange')}
                </Typography>
                <DateRangePicker
                  startDate={exportParams.startDate}
                  endDate={exportParams.endDate}
                  onChange={handleExportDateRangeChange}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: 'var(--mut)', mb: 0.5 }}>
                  {t('transactions.cashoutIdOptional')}
                </Typography>
                <Autocomplete
                  options={cycleOptions}
                  fullWidth
                  onChange={(_, value) => setExportParams({ ...exportParams, cashoutId: value?.title || undefined })}
                  getOptionLabel={(cashCycle) => cashCycle.title || ''}
                  renderInput={(params) => (
                    <MainInput {...params} margin="normal" fullWidth placeholder={t('transactions.selectCashoutId')} />
                  )}
                  value={cycleOptions.find((c) => c.title === exportParams.cashoutId) || null}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <MUIButton onClick={() => setExportModalOpen(false)} sx={{ color: 'var(--tx)' }}>
            {t('common.cancel')}
          </MUIButton>
          <MUIButton
            onClick={handleExportConfirm}
            disabled={
              !exportParams.startDate ||
              !exportParams.endDate ||
              isWaiting ||
              (exportParams.startDate && !exportParams.startDate.isValid()) ||
              (exportParams.endDate && !exportParams.endDate.isValid())
            }
            sx={{ ...main_button, textTransform: 'none' }}
          >
            {isWaiting ? t('transactions.exporting') : t('common.export')}
          </MUIButton>
        </DialogActions>
      </Dialog>

      <CreateTicketModal
        open={helpTicketOpen}
        onClose={() => { setHelpTicketOpen(false); setHelpTicketContext(null); }}
        token={token}
        context={helpTicketContext}
        onSuccess={() => setHelpTicketOpen(false)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading,
  isWaiting: state?.wait?.isLoading,
  token: state?.auth?.token,
  transactions: state?.records?.transactions?.docs,
  transactionsPagination: state?.records?.transactions,
  payoutDue: state?.records?.payoutDue,
  expectedIncome: state?.records?.expectedIncome,
  totalIncome: state?.records?.totalIncome,
  totalFees: state?.records?.totalFees,
  totalPayout: state?.records?.totalPayout,
  totalHold: state?.records?.totalHold,
  cashCycles: state?.records?.cashCycles,
  payouts: state?.records?.payouts,
  fees: state?.records?.fees
});

const mapDispatchToProps = { getTransactions, getNextPayout, getCashCycles, exportTransactions };

export default connect(mapStateToProps, mapDispatchToProps)(Transactions);
